/**
 * Typed API client for OAuth 2.0 registered clients (epic 200).
 *
 * Tenant admins register confidential/public OAuth clients that drive the
 * authorization-code + PKCE flow. The client secret (confidential clients
 * only) is returned once on creation / rotation and never again — the UI
 * surfaces it in a reveal-once banner.
 */

import { apiClient } from '@/lib/apiClient';
import { rootClient } from '@/lib/rootClient';

export interface ClientSummary {
  id: string;
  name: string;
  clientId: string;
  redirectUris: string[];
  allowedScopes: string[];
  isPublished: boolean;
  logoUrl: string | null;
  homepageUrl: string | null;
  privacyPolicyUrl: string | null;
  enabled: boolean;
  createdAt: string;
}

export interface CreateClientRequest {
  name: string;
  redirectUris: string[];
  allowedScopes: string[];
  isPublic: boolean;
  logoUrl?: string | null;
  homepageUrl?: string | null;
  privacyPolicyUrl?: string | null;
}

/** All fields optional — only the provided ones are patched. */
export interface UpdateClientRequest {
  name?: string;
  redirectUris?: string[];
  allowedScopes?: string[];
  logoUrl?: string | null;
  homepageUrl?: string | null;
  privacyPolicyUrl?: string | null;
  enabled?: boolean;
}

/** `clientSecret` is present only for confidential (isPublic=false) clients. */
export interface CreatedClient {
  id: string;
  clientId: string;
  clientSecret?: string;
}

export async function listOAuthClients(): Promise<ClientSummary[]> {
  const { data } = await apiClient.get<ClientSummary[]>('/admin/oauth-clients');
  return data;
}

export async function getOAuthClient(id: string): Promise<ClientSummary> {
  const { data } = await apiClient.get<ClientSummary>(`/admin/oauth-clients/${id}`);
  return data;
}

export async function createOAuthClient(req: CreateClientRequest): Promise<CreatedClient> {
  const { data } = await apiClient.post<CreatedClient>('/admin/oauth-clients', req);
  return data;
}

export async function updateOAuthClient(
  id: string,
  req: UpdateClientRequest,
): Promise<ClientSummary> {
  const { data } = await apiClient.patch<ClientSummary>(`/admin/oauth-clients/${id}`, req);
  return data;
}

export async function deleteOAuthClient(id: string): Promise<void> {
  await apiClient.delete(`/admin/oauth-clients/${id}`);
}

export async function rotateOAuthClientSecret(id: string): Promise<{ clientSecret: string }> {
  const { data } = await apiClient.post<{ clientSecret: string }>(
    `/admin/oauth-clients/${id}/rotate-secret`,
  );
  return data;
}

// ── User-facing consent flow (root-mounted /oauth/*, epic 200). ──

export interface ConsentMetadata {
  clientId: string;
  appName: string;
  logoUrl: string | null;
  scopes: string[];
  redirectUri: string;
  state: string;
}

export interface GrantRequest {
  clientId: string;
  redirectUri: string;
  scope: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  approve: boolean;
}

export interface AuthorizeParams {
  clientId: string;
  redirectUri: string;
  scope: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
}

/** Fetch the consent metadata for an in-flight authorization-code request. */
export async function getConsentMetadata(p: AuthorizeParams): Promise<ConsentMetadata> {
  const { data } = await rootClient.get<ConsentMetadata>('/oauth/authorize', {
    params: {
      client_id: p.clientId,
      redirect_uri: p.redirectUri,
      response_type: 'code',
      scope: p.scope,
      state: p.state,
      code_challenge: p.codeChallenge,
      code_challenge_method: p.codeChallengeMethod,
    },
  });
  return data;
}

/** Approve or deny the request; returns the redirect URL to navigate to. */
export async function grantConsent(req: GrantRequest): Promise<{ redirectUrl: string }> {
  const { data } = await rootClient.post<{ redirectUrl: string }>('/oauth/authorize/grant', req);
  return data;
}
